import { atom, useAtom } from 'jotai';
import { useEffect } from 'react';

import { captureWarning } from '../../logger/Logger';
import {
    type GuidanceSession,
    type GuidanceSessionType,
} from '../types/GuidanceSessions';
import { useGetGuidanceSessions } from './useGetGuidanceSessions';
import { useMembershipGuidanceSessionInfo } from './useMembershipGuidanceSessionInfo';

const nextGuidanceSessionMismatchWarnedAtom = atom(false);
/**
 * Provides information about the Guidance Session that should come next for this membership:
 * the type of the next session, whether it needs to be scheduled, and if already scheduled
 * then the instance of that Guidance Session.
 * Does not include check-ins or other ad-hoc sessions.
 */
export const useGetNextGuidanceSession = (): {
    isLoading: boolean;
    isFetching: boolean;
    nextGuidanceSession: GuidanceSession | undefined;
    nextGuidanceOnboardingSessionType: GuidanceSessionType | null | undefined;
    nextGuidanceOnboardingSessionNeedsBooking: boolean | undefined;
} => {
    const {
        nextGuidanceOnboardingSessionType:
            expectedNextGuidanceOnboardingSessionType,
        isLoading: isLoadingNextSessionType,
    } = useGetNextGuidanceOnboardingSessionType();
    const {
        upcomingSessions = [],
        isLoadingSessions,
        isFetchingSessions,
    } = useGetGuidanceSessions('upcoming');

    const [
        nextGuidanceSessionMismatchWarned,
        setNextGuidanceSessionMismatchWarned,
    ] = useAtom(nextGuidanceSessionMismatchWarnedAtom);

    useEffect(() => {
        if (nextGuidanceSessionMismatchWarned) {
            return;
        }

        const nextGuidanceSessionType = upcomingSessions[0]?.type;
        const nextGuidanceSessionStatus = upcomingSessions[0]?.status;
        if (
            nextGuidanceSessionType != null &&
            nextGuidanceSessionType !==
                expectedNextGuidanceOnboardingSessionType
        ) {
            captureWarning(
                `Next guidance session type mismatch. Expected ${expectedNextGuidanceOnboardingSessionType} but the next scheduled session is ${nextGuidanceSessionType}. Scheduled session status is ${nextGuidanceSessionStatus}.`,
            );
            setNextGuidanceSessionMismatchWarned(true);
        }
    }, [
        nextGuidanceSessionMismatchWarned,
        upcomingSessions[0]?.type,
        expectedNextGuidanceOnboardingSessionType,
    ]);

    if (isLoadingNextSessionType || isLoadingSessions) {
        return {
            isLoading: isLoadingNextSessionType || isLoadingSessions,
            isFetching: isFetchingSessions,
            nextGuidanceSession: undefined,
            nextGuidanceOnboardingSessionType: undefined,
            nextGuidanceOnboardingSessionNeedsBooking: undefined,
        };
    }

    const nextGuidanceSession = upcomingSessions[0];

    const nextGuidanceOnboardingSessionType =
        nextGuidanceSession?.type ?? expectedNextGuidanceOnboardingSessionType;

    const nextGuidanceOnboardingSessionNeedsBooking =
        nextGuidanceSession == null &&
        expectedNextGuidanceOnboardingSessionType != null;

    return {
        isLoading: isLoadingSessions,
        isFetching: isFetchingSessions,
        nextGuidanceSession,
        nextGuidanceOnboardingSessionType,
        nextGuidanceOnboardingSessionNeedsBooking,
    };
};

/**
 * Provides the expected next Guidance Session type, by comparing past sessions of this membership
 * to the expected ordered set of Guidance Sessions.
 */
export const useGetNextGuidanceOnboardingSessionType = () => {
    const {
        getNextGuidanceOnboardingSessionType,
        hasCompletedGuidanceOnboarding,
    } = useMembershipGuidanceSessionInfo();
    const { pastSessions = [], isLoadingSessions } =
        useGetGuidanceSessions('past');
    const nextGuidanceOnboardingSessionType = isLoadingSessions
        ? null
        : getNextGuidanceOnboardingSessionType(pastSessions);

    return {
        isLoading: isLoadingSessions,
        nextGuidanceOnboardingSessionType,
        hasCompletedGuidanceOnboarding:
            hasCompletedGuidanceOnboarding(pastSessions),
    };
};
